import { mergeAttributes, Node, NodeConfig } from '@tiptap/core';
import { getNodesOfTypeForRange } from './utils/nodeHelpers';

/**
 * Fill In The Blanks Word - Custom Extension
 */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fillInTheBlanksWord: {
      /**
       * Insert a fill in the blanks word
       */
      insertFillInTheBlanksWord: (word?: string) => ReturnType;
      removeFillInTheBlanksWord: (startPos: number) => ReturnType;
    };
  }
}

export const fillInTheBlanksBaseExtensionConfig: Partial<NodeConfig<unknown, unknown>> = {
  name: 'fillInTheBlanksWord',
  inline: true,
  group: 'inline',
  content: 'inline*',
  draggable: true,
  addAttributes() {
    return {
      id: {
        default: null, // will be set by @tiptap/extension-unique-id
        parseHTML: (element) => element.dataset.id,
        renderHTML: (attrs) => {
          return attrs.id ? { 'data-id': attrs.id } : {};
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span[data-type="fill-in-the-blanks-word"]',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'fill-in-the-blanks-word' }), 0];
  },
  addCommands() {
    return {
      insertFillInTheBlanksWord:
        (word?: string) =>
        ({ commands, state }) => {
          const allFillInTheBlanksWords = getNodesOfTypeForRange({
            node: state.doc,
            type: this.name,
            from: 0,
            to: state.doc.content.size,
          });

          if (allFillInTheBlanksWords.length >= 100) {
            return false;
          }
          const { from, to } = state.selection;
          const fillInTheBlankNodes = getNodesOfTypeForRange({ node: state.doc, type: this.name, from, to });

          // Prevent nesting
          if (fillInTheBlankNodes.length > 0) return false;

          // Get the selected content as a slice (preserves marks/formatting)
          const selectedContent = state.doc.slice(from, to).content;

          // If nothing is selected, fallback to plain text or empty
          const content = selectedContent.size > 0 ? selectedContent : state.schema.text(word || '');

          // Replace selection with the new node, wrapping the content
          return commands.insertContent({
            type: this.name,
            content: content.toJSON(),
          });
        },
      removeFillInTheBlanksWord:
        (startPos: number) =>
        ({ commands, state }) => {
          const from = startPos;
          const to = startPos + 1;

          const fillInTheBlankNodes = getNodesOfTypeForRange({ node: state.doc, type: this.name, from, to });
          if (fillInTheBlankNodes.length === 0) return false;

          // should only ever be one
          const firstNode = fillInTheBlankNodes[0];

          const { pos, content, nodeSize } = firstNode;

          // Insert the content back with all its formatting preserved
          return commands.insertContentAt({ from: pos, to: pos + nodeSize }, content.toJSON(), {
            updateSelection: true,
          });
        },
    };
  },
};

/**
 * This base extension does not support a view, this will be extended per application
 */
export const fillInTheBlanksBaseExtension = Node.create(fillInTheBlanksBaseExtensionConfig);
